import { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageSquare, Zap, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { AI_EMPLOYEES } from '../../data/employees';
import { useThemeStore } from '../../store/theme';
import {
  AriaCharacter,
  NovaCharacter,
  AtlasCharacter,
  SparkCharacter,
  EchoCharacter,
  CodeCharacter,
  InkCharacter,
  OrbitCharacter,
  SageCharacter,
  ScoutCharacter,
  PulseCharacter,
  FluxCharacter,
  SyncCharacter,
  PixelCharacter,
  LegalCharacter,
} from './AICharacters';

// Map employee IDs to their character components
const characterMap: { [key: string]: React.ComponentType<{ size?: 'sm' | 'md' | 'lg' }> } = {
  aria: AriaCharacter,
  nova: NovaCharacter,
  atlas: AtlasCharacter,
  spark: SparkCharacter,
  echo: EchoCharacter,
  code: CodeCharacter,
  ink: InkCharacter,
  orbit: OrbitCharacter,
  sage: SageCharacter,
  scout: ScoutCharacter,
  pulse: PulseCharacter,
  flux: FluxCharacter,
  sync: SyncCharacter,
  pixel: PixelCharacter,
  legal: LegalCharacter,
};

// Category filters - counts match actual employees in data/employees.ts
const categories = [
  { id: 'all', label: 'All Employees', count: 15 },
  { id: 'marketing', label: 'Marketing', count: 3 }, // Aria, Echo, Orbit
  { id: 'sales', label: 'Sales', count: 2 }, // Flux, Spark
  { id: 'support', label: 'Support', count: 1 }, // Nova
  { id: 'operations', label: 'Operations', count: 4 }, // Atlas, Sage, Scout, Legal
  { id: 'creative', label: 'Creative', count: 2 }, // Ink, Pixel
  { id: 'technical', label: 'Technical', count: 1 }, // Code
  { id: 'personal', label: 'Personal', count: 2 }, // Pulse, Sync
];

// Employee card with hover effects
const EmployeeCard = ({
  employee,
  index,
  isSelected,
  onClick,
  isDark
}: {
  employee: typeof AI_EMPLOYEES[0];
  index: number;
  isSelected: boolean;
  onClick: () => void;
  isDark: boolean;
}) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ delay: index * 0.05 }}
      onClick={onClick}
      className={`group relative cursor-pointer ${isSelected ? 'lg:col-span-2 lg:row-span-2' : ''}`}
    >
      <div className={`
        relative overflow-hidden rounded-2xl transition-all duration-500
        ${isSelected
          ? isDark
            ? 'bg-dark-800 border-2 border-accent-purple shadow-glow-sm'
            : 'bg-white border-2 border-accent-purple shadow-lg'
          : isDark
            ? 'bg-dark-800/50 border border-white/5 hover:border-white/20 hover:bg-dark-800'
            : 'bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md shadow-sm'
        }
      `}>
        {/* Gradient overlay on hover */}
        <div className={`
          absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500
          bg-gradient-to-br ${employee.gradient}
        `} style={{ opacity: 0.05 }} />

        <div className={`p-5 ${isSelected ? 'lg:p-8' : ''}`}>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <motion.div
              className={`flex items-center justify-center overflow-visible
                ${isSelected ? 'scale-75' : 'scale-50'}`}
              whileHover={{ scale: isSelected ? 0.8 : 0.55 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              {characterMap[employee.id] ? (
                (() => {
                  const CharacterComponent = characterMap[employee.id];
                  return <CharacterComponent size="sm" />;
                })()
              ) : (
                <div className={`rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center w-12 h-12`}>
                  <span className="font-bold text-white text-lg">{employee.avatar}</span>
                </div>
              )}
            </motion.div>
            <div className="flex gap-2">
              {employee.isNew && (
                <motion.span
                  className="px-2 py-1 text-xs font-medium bg-accent-green/20 text-accent-green rounded-full"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  NEW
                </motion.span>
              )}
            </div>
          </div>

          {/* Info */}
          <h3 className={`font-bold mb-1 ${isSelected ? 'text-2xl' : 'text-lg'} ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {employee.name}
          </h3>
          <p className={`text-accent-cyan mb-3 ${isSelected ? 'text-base' : 'text-sm'}`}>
            {employee.role}
          </p>
          <p className={`mb-4 ${isSelected ? 'text-base' : 'text-sm line-clamp-2'} ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
            {employee.description}
          </p>

          {/* Capabilities */}
          {isSelected && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <div className={`text-xs uppercase tracking-wider mb-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Capabilities</div>
              <div className="flex flex-wrap gap-2">
                {employee.capabilities.map((cap, i) => (
                  <motion.span
                    key={cap}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.05 }}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${isDark ? 'bg-dark-700 border-white/5 text-text-secondary' : 'bg-slate-100 border-slate-200 text-slate-700'}`}
                  >
                    {cap}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Action */}
          <div className="flex items-center gap-3">
            <Link
              to={`/register?employee=${employee.id}`}
              className={`
                flex items-center gap-2 font-medium transition-all
                ${isSelected
                  ? 'px-5 py-2.5 bg-gradient-accent rounded-xl text-white hover:shadow-glow-sm'
                  : isDark
                    ? 'text-text-muted group-hover:text-accent-purple'
                    : 'text-slate-500 group-hover:text-accent-purple'
                }
              `}
              onClick={(e) => e.stopPropagation()}
            >
              <MessageSquare className="w-4 h-4" />
              <span>Chat with {employee.name}</span>
              {!isSelected && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Sample use cases for display (since employee data doesn't include useCases)
const sampleUseCases = [
  { id: '1', title: 'Quick Task Execution', description: 'Get instant help with common tasks and workflows.' },
  { id: '2', title: 'Content Generation', description: 'Create high-quality content tailored to your needs.' },
  { id: '3', title: 'Data Analysis', description: 'Analyze and interpret data for actionable insights.' },
];

// Use case carousel
const UseCaseCarousel = ({ employee, isDark }: { employee: typeof AI_EMPLOYEES[0]; isDark: boolean }) => {
  const [activeUseCase, setActiveUseCase] = useState(0);
  const useCases = sampleUseCases;

  return (
    <div className={`p-6 lg:p-8 rounded-2xl border ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
      <div className="flex items-center justify-between mb-6">
        <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Use Cases</h4>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveUseCase(prev => Math.max(0, prev - 1))}
            disabled={activeUseCase === 0}
            className={`p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${isDark ? 'bg-dark-700 hover:bg-dark-600' : 'bg-slate-100 hover:bg-slate-200'}`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setActiveUseCase(prev => Math.min(useCases.length - 1, prev + 1))}
            disabled={activeUseCase === useCases.length - 1}
            className={`p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${isDark ? 'bg-dark-700 hover:bg-dark-600' : 'bg-slate-100 hover:bg-slate-200'}`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {useCases.length > 0 && (
          <motion.div
            key={activeUseCase}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-accent-purple/20 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-accent-purple" />
              </div>
              <div>
                <h5 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {useCases[activeUseCase]?.title || 'Quick Task'}
                </h5>
                <p className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  {useCases[activeUseCase]?.description || 'Get instant help with common tasks.'}
                </p>
              </div>
            </div>

            <div className="pl-14 space-y-2">
              {employee.capabilities.slice(0, 3).map((cap: string, i: number) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-accent-green" />
                  <span className={isDark ? 'text-text-muted' : 'text-slate-500'}>{cap}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-4 pt-2">
              <Link
                to={`/register?employee=${employee.id}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isDark ? 'bg-dark-700 hover:bg-dark-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-900'}`}
              >
                <Zap className="w-4 h-4" />
                Try This Task
              </Link>
              <div className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                {activeUseCase + 1} of {useCases.length} use cases
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dots indicator */}
      <div className="flex justify-center gap-1.5 mt-6">
        {useCases.slice(0, 6).map((_, i: number) => (
          <button
            key={i}
            onClick={() => setActiveUseCase(i)}
            className={`w-2 h-2 rounded-full transition-all ${
              i === activeUseCase ? 'bg-accent-purple w-6' : isDark ? 'bg-dark-600 hover:bg-dark-500' : 'bg-slate-300 hover:bg-slate-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default function EmployeeShowcase() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);

  const filteredEmployees = selectedCategory === 'all'
    ? AI_EMPLOYEES
    : AI_EMPLOYEES.filter(e => e.category === selectedCategory);

  const selectedEmployeeData = AI_EMPLOYEES.find(e => e.id === selectedEmployee);

  return (
    <section ref={ref} id="employees" className="py-24 lg:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-purple/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-cyan/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent-purple/10 border border-accent-purple/20 rounded-full text-sm text-accent-purple mb-6"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-purple opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-purple"></span>
            </span>
            15 Specialized AI Employees
          </motion.div>
          <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Meet Your New Team
          </h2>
          <p className={`text-xl max-w-2xl mx-auto ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
            Each AI employee is an expert in their field, ready to handle your tasks with precision and creativity.
          </p>
        </motion.div>

        {/* Category Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                setSelectedCategory(cat.id);
                setSelectedEmployee(null);
              }}
              className={`
                px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300
                ${selectedCategory === cat.id
                  ? 'bg-accent-purple text-white shadow-glow-sm'
                  : isDark
                    ? 'bg-dark-800 text-text-secondary hover:bg-dark-700 hover:text-white border border-white/5'
                    : 'bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-slate-200 shadow-sm'
                }
              `}
            >
              {cat.label}
              <span className={`ml-2 ${selectedCategory === cat.id ? 'text-white/70' : isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                {cat.count}
              </span>
            </button>
          ))}
        </motion.div>

        {/* Employee Grid & Details */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Grid */}
          <div className={`${selectedEmployee ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
            <motion.div
              layout
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              <AnimatePresence mode="popLayout">
                {filteredEmployees.map((employee, index) => (
                  <EmployeeCard
                    key={employee.id}
                    employee={employee}
                    index={index}
                    isSelected={selectedEmployee === employee.id}
                    onClick={() => setSelectedEmployee(
                      selectedEmployee === employee.id ? null : employee.id
                    )}
                    isDark={isDark}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          </div>

          {/* Details Panel */}
          <AnimatePresence>
            {selectedEmployee && selectedEmployeeData && (
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                className="lg:col-span-1 space-y-6"
              >
                <UseCaseCarousel employee={selectedEmployeeData} isDark={isDark} />

                {/* Quick actions */}
                <div className={`p-6 rounded-2xl border ${isDark ? 'bg-dark-800/80 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
                  <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Quick Actions</h4>
                  <div className="space-y-3">
                    <Link
                      to={`/register?employee=${selectedEmployeeData.id}`}
                      className={`flex items-center justify-between p-3 rounded-xl transition-colors group ${isDark ? 'bg-dark-700/50 hover:bg-dark-700' : 'bg-slate-50 hover:bg-slate-100'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-accent-purple/20 flex items-center justify-center">
                          <MessageSquare className="w-4 h-4 text-accent-purple" />
                        </div>
                        <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>Start Conversation</span>
                      </div>
                      <ArrowRight className={`w-4 h-4 group-hover:translate-x-1 transition-all ${isDark ? 'text-text-muted group-hover:text-white' : 'text-slate-400 group-hover:text-slate-900'}`} />
                    </Link>
                    <Link
                      to="/register"
                      className={`flex items-center justify-between p-3 rounded-xl transition-colors group ${isDark ? 'bg-dark-700/50 hover:bg-dark-700' : 'bg-slate-50 hover:bg-slate-100'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-accent-cyan/20 flex items-center justify-center">
                          <Zap className="w-4 h-4 text-accent-cyan" />
                        </div>
                        <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>One-Click Tasks</span>
                      </div>
                      <ArrowRight className={`w-4 h-4 group-hover:translate-x-1 transition-all ${isDark ? 'text-text-muted group-hover:text-white' : 'text-slate-400 group-hover:text-slate-900'}`} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="text-center mt-16"
        >
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-accent rounded-2xl font-semibold text-white text-lg
                     hover:shadow-glow-md transition-all hover:scale-105"
          >
            <span>Hire Your AI Team</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className={`mt-4 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Start free • No credit card required</p>
        </motion.div>
      </div>
    </section>
  );
}
